import { useEditor, EditorContent, Editor, JSONContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Code } from 'lucide-react'
import { VariableExtension } from './VariableExtension'
import VariablePicker from './VariablePicker'
import { Variable, getVariableByKey } from '@/lib/variables'

interface VariableEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
}

export interface VariableEditorRef {
  editor: Editor | null
}

// Parse stored format ({{key|fallback}}) to Tiptap JSON
function parseStoredContent(text: string): JSONContent {
  if (!text) {
    return {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    }
  }

  const variableRegex = /\{\{([^|}]+)(?:\|([^}]*))?\}\}/g
  const parts: JSONContent[] = []
  let lastIndex = 0
  let match

  while ((match = variableRegex.exec(text)) !== null) {
    // Add text before the variable
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        text: text.slice(lastIndex, match.index),
      })
    }

    // Add the variable node
    const key = match[1]
    const fallback = match[2] || null
    const variable = getVariableByKey(key)

    parts.push({
      type: 'variable',
      attrs: {
        key,
        label: variable?.label || key,
        fallback,
      },
    })

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      text: text.slice(lastIndex),
    })
  }

  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: parts.length > 0 ? parts : undefined,
      },
    ],
  }
}

// Convert editor JSON back to stored format
function serializeContent(editor: Editor): string {
  let result = ''

  editor.state.doc.descendants((node) => {
    if (node.isText) {
      result += node.text
    } else if (node.type.name === 'variable') {
      const { key, fallback } = node.attrs
      result += fallback ? `{{${key}|${fallback}}}` : `{{${key}}}`
    } else if (node.type.name === 'paragraph') {
      // Add newlines for paragraph breaks (except for the first one)
      if (result.length > 0 && !result.endsWith('\n')) {
        result += '\n'
      }
    }
  })

  return result.trim()
}

const VariableEditor = forwardRef<VariableEditorRef, VariableEditorProps>(
  ({ value, onChange, placeholder = 'Escreva sua mensagem...', rows = 5 }, ref) => {
    const [showPicker, setShowPicker] = useState(false)
    const [isFocused, setIsFocused] = useState(false)

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          bulletList: false,
          orderedList: false,
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
          code: false,
        }),
        VariableExtension,
      ],
      content: parseStoredContent(value),
      onUpdate: ({ editor }) => {
        const serialized = serializeContent(editor)
        onChange(serialized)
      },
      onFocus: () => setIsFocused(true),
      onBlur: () => {
        // Delay to allow clicking toolbar buttons before hiding
        setTimeout(() => {
          if (!showPicker) setIsFocused(false)
        }, 150)
      },
      editorProps: {
        attributes: {
          class: `prose prose-sm max-w-none prose-p:my-0 prose-p:leading-normal prose-img:my-0 focus:outline-none p-3 min-h-[${rows * 24}px]`,
          style: `min-height: ${rows * 24}px`,
        },
      },
    })

    useImperativeHandle(ref, () => ({ editor }))

    const handleInsertVariable = useCallback(
      (variable: Variable) => {
        if (editor) {
          editor.commands.insertVariable({
            key: variable.key,
            label: variable.label,
            fallback: null,
          })
          editor.commands.focus()
          setIsFocused(true)
        }
        setShowPicker(false)
      },
      [editor]
    )

    // Update editor content when value changes externally
    useEffect(() => {
      if (editor && !editor.isFocused) {
        const currentContent = serializeContent(editor)
        if (currentContent !== value) {
          editor.commands.setContent(parseStoredContent(value))
        }
      }
    }, [value, editor])

    // Close picker when clicking outside
    useEffect(() => {
      if (!showPicker) return

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement
        if (!target.closest('[data-variable-picker]') && !target.closest('[data-variable-trigger]')) {
          setShowPicker(false)
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [showPicker])

    return (
      <div className="relative">
        <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white">
          <EditorContent
            editor={editor}
            className="[&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none"
            data-placeholder={placeholder}
          />

          {/* Toolbar - Only visible when focused or picker is open */}
          {(isFocused || showPicker) && (
            <div className="border-t border-gray-200 px-3 py-2 flex items-center gap-2">
              <button
                type="button"
                data-variable-trigger
                onClick={() => setShowPicker(!showPicker)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <Code className="w-4 h-4" />
                Inserir variavel
              </button>
            </div>
          )}
        </div>

        {showPicker && (
          <div data-variable-picker>
            <VariablePicker onSelect={handleInsertVariable} onClose={() => setShowPicker(false)} />
          </div>
        )}
      </div>
    )
  }
)

VariableEditor.displayName = 'VariableEditor'
export default VariableEditor
